import {
  Box,
  Button,
  Chip,
  Dropdown,
  DropdownItem,
  Modal,
  ModalActions,
  ModalContent,
  ModalTitle,
  TextField,
} from '@cambridgeassessment/cambridge-ui';
import { BusinessStreams } from 'ciam-commons';
import React, { useEffect, useState } from 'react';
import { hot } from 'react-hot-loader/root';
import { businessUnits } from 'src/constants';
import useOrgUserDetails from 'src/hooks/useOrgUserDetails';
import { useStyles } from './styles';

export type UserDetailsModalProps = {
  open: boolean;
  onClose: () => void;
  variant: 'creditorNumber' | 'businessUnit' | 'examinerId';
};

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({
  open,
  onClose,
  variant,
}) => {
  const classes = useStyles();
  const {
    deleteUserCreditorNumber,
    deleteUserBusinessStream,
    updateUserBusinessStream,
    updateUserCreditorNumber,
    updateUserExaminerId,
    userDetails,
  } = useOrgUserDetails();
  const [selectedCreditorNumber, setSelectedCreditorNumber] =
    useState<string>('');
  const [selectedBusinessUnits, setSelectedBusinessUnits] = useState<
    BusinessStreams[]
  >([]);
  const [selectedExaminerId, setSelectedExaminerId] = useState<string>('');

  useEffect(() => {
    setSelectedCreditorNumber(userDetails.creditorNumber);
    setSelectedBusinessUnits(userDetails.businessStream);
    setSelectedExaminerId(userDetails.examinerId);
  }, [open]);

  const handleSelectBusinessUnits = (_e, value): void => {
    setSelectedBusinessUnits(value);
  };

  const handleRemoveBusinessUnits = (businessUnit) => (): void =>
    setSelectedBusinessUnits((prev) =>
      prev.filter(
        (selectedBusinessUnit) => selectedBusinessUnit !== businessUnit
      )
    );

  const handleUpdateUserExaminerId = () => {
    if (userDetails.systemIDs && userDetails.systemIDs.length) {
      const examinerId = userDetails.systemIDs.find(
        (x) => x.idType === 'examinerId'
      );
      if (examinerId) {
        const examinerIdIndex = userDetails.systemIDs.findIndex(
          (x) => x.idType === 'examinerId'
        );
        userDetails.systemIDs[examinerIdIndex].idValue = selectedExaminerId;
      } else {
        userDetails.systemIDs.push({
          idType: 'examinerId',
          idValue: selectedExaminerId,
        });
      }
    } else {
      userDetails.systemIDs = [
        { idType: 'examinerId', idValue: selectedExaminerId },
      ];
    }
    updateUserExaminerId(userDetails.systemIDs);
  };

  const handleSubmit = async () => {
    try {
      if (variant === 'examinerId') {
        handleUpdateUserExaminerId();
      }
      if (variant === 'creditorNumber') {
        if (selectedCreditorNumber) {
          updateUserCreditorNumber(selectedCreditorNumber);
        } else {
          deleteUserCreditorNumber();
        }
      }
      if (variant === 'businessUnit') {
        if (selectedBusinessUnits.length) {
          updateUserBusinessStream(selectedBusinessUnits);
        } else {
          deleteUserBusinessStream();
        }
      }
    } finally {
      onClose();
    }
  };

  let modalTitle = 'Edit ';
  if (variant === 'creditorNumber') modalTitle += 'creditor number for ';
  if (variant === 'businessUnit') modalTitle += 'business unit(s) for ';
  if (variant === 'examinerId') modalTitle += 'examiner ID for ';
  modalTitle += `${userDetails?.firstName}  ${userDetails?.lastName} `;

  const saveButtonStatus = () => {
    if (variant === 'creditorNumber') {
      return selectedCreditorNumber?.length > 64;
    } else if (variant === 'examinerId') {
      return !new RegExp(/^[A-Z]{2}\d{2}[A-Z]{2}$/).test(selectedExaminerId);
    } else {
      return false;
    }
  };

  return (
    <Modal open={open} onClose={(): void => onClose()}>
      <ModalTitle
        onClose={(): void => onClose()}
        className={classes.modalTitle}
        data-testid="userDetails-modalTitle"
      >
        {modalTitle}
      </ModalTitle>
      <ModalContent>
        {variant === 'examinerId' && (
          <TextField
            label="Examiner ID"
            data-testid="examinerId-modalInput"
            value={selectedExaminerId}
            onChange={(e) => {
              setSelectedExaminerId(e.target.value);
            }}
            validationType={
              !new RegExp(/^[A-Z]{2}\d{2}[A-Z]{2}$/).test(selectedExaminerId)
                ? 'error'
                : null
            }
            helperText={
              !new RegExp(/^[A-Z]{2}\d{2}[A-Z]{2}$/).test(selectedExaminerId)
                ? "Invalid format for Examiner ID (eg. 'AB09YZ')"
                : null
            }
          />
        )}
        {variant === 'creditorNumber' && (
          <TextField
            label="Creditor number"
            data-testid="creditorNumber-modalInput"
            value={selectedCreditorNumber}
            onChange={(e) => {
              setSelectedCreditorNumber(e.target.value);
            }}
            validationType={
              selectedCreditorNumber?.length > 64 ? 'error' : null
            }
            helperText={
              selectedCreditorNumber?.length > 64
                ? 'Maximum 64 characters'
                : null
            }
          />
        )}
        {variant === 'businessUnit' && (
          <>
            <Dropdown
              placeholder="Please select one or more business units"
              label="Business unit"
              onChange={handleSelectBusinessUnits}
              value={selectedBusinessUnits}
              multiple
              data-testid="businessUnit-modalInput"
            >
              {businessUnits.map((businessUnit) => (
                <DropdownItem key={businessUnit} value={businessUnit}>
                  {businessUnit}
                </DropdownItem>
              ))}
            </Dropdown>
            <Box mt={2}>
              {selectedBusinessUnits?.map((businessUnit) => (
                <Chip
                  key={businessUnit}
                  tabIndex={-1}
                  label={businessUnit}
                  className={classes.chip}
                  onDelete={handleRemoveBusinessUnits(businessUnit)}
                  truncate
                />
              ))}
            </Box>
          </>
        )}
      </ModalContent>
      <ModalActions>
        <Button
          onClick={(): void => onClose()}
          color="primary"
          variant="outlined"
          data-testid="cancelButton"
        >
          Cancel
        </Button>
        <Button
          onClick={() => handleSubmit()}
          color="primary"
          data-testid="saveButton"
          disabled={saveButtonStatus()}
        >
          Save changes
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default hot(UserDetailsModal);
